import React, { useCallback, useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import classNames from "classnames";
import { useUpdateRulePosition } from "../../../../api/rules.api";
import { useWorkspaceContext } from "../../../../state/workspaceContext";
import { Counterparty } from "../../../../common/types/counterparty";
import styles from "../RulesTable.module.scss";

import { TransactionRuleGroup } from "../../../../common/types/transactionRule";
import { useTypedFlags } from "../../../../hooks/useTypedFlags";

import { SortableGroup } from "./SortableGroup";
import { RulesGroupBody } from "./RulesGroupBody";
import { GroupOverlay } from "./GroupOverlay";
import { RuleOverlay } from "./RuleOverlay";
import { getNewOrderForRuleReorder } from "./utils";
import { AccountingRulesTable } from "./AccountingRulesTable";

interface RulesTableProps {
    groups: TransactionRuleGroup[];
    accountingGroup?: TransactionRuleGroup;
    counterparties: Counterparty[];
    transactionCounts: Record<string, number>;
}

export const RulesTable: React.FC<RulesTableProps> = ({
    groups,
    counterparties,
    transactionCounts,
    accountingGroup,
}) => {
    const { advancedRules } = useTypedFlags();
    const { activeWorkspaceId } = useWorkspaceContext();
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    const updateRulePosition = useUpdateRulePosition();

    const sensors = useSensors(useSensor(PointerSensor));

    const findGroupByRuleId = useCallback(
        (ruleId: string) =>
            groups.find((g) => g.rules?.some((r) => r.id === ruleId)),
        [groups],
    );

    const findRuleById = useCallback(
        (ruleId: string) => {
            for (const g of groups) {
                const rule = g.rules?.find((r) => r.id === ruleId);
                if (rule) {
                    return rule;
                }
            }
            return null;
        },
        [groups],
    );

    const findGroupById = useCallback(
        (groupId: string) => groups.find((g) => g.id === groupId),
        [groups],
    );

    const handleDragStart = (event: any) => {
        setActiveId(event.active.id);
    };

    const handleDragOver = (event: any) => {
        setOverId(event.over?.id ?? null);
    };

    const handleDragEnd = async (event: any) => {
        setActiveId(null);
        setOverId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        // Group reordering
        const activeGroupIndex = groups.findIndex((g) => g.id === active.id);
        // if we have activeGroupIndex, we must find the overGroupIndex, either via groupId, or via containing ruleId
        let overGroupIndex = groups.findIndex((g) => g.id === over.id);

        if (overGroupIndex === -1) {
            overGroupIndex = groups.findIndex((g) =>
                g.rules?.some((r) => r.id === over.id),
            );
        }
        if (activeGroupIndex !== -1 && overGroupIndex !== -1) {
            // We do not support this yet
            return;
        }

        // Rule reordering or moving between groups
        const sourceGroup = findGroupByRuleId(active.id);
        const targetGroup =
            findGroupByRuleId(over.id) ?? findGroupById(over.id);
        if (!sourceGroup || !targetGroup) {
            return;
        }

        const sourceIndex = sourceGroup.rules?.findIndex(
            (r) => r.id === active.id,
        );
        const targetIndex = targetGroup.rules?.findIndex(
            (r) => r.id === over.id,
        );

        if (
            sourceIndex === undefined ||
            targetIndex === undefined ||
            targetIndex < 0
        ) {
            return;
        }

        const newOrder = getNewOrderForRuleReorder({
            sourceGroup,
            targetGroup,
            sourceRuleIndex: sourceIndex,
            targetRuleIndex: targetIndex,
        });

        const rule = findRuleById(active.id);
        if (!rule) {
            return;
        }

        await updateRulePosition.mutateAsync({
            workspaceId: activeWorkspaceId ?? "",
            rule: { ...rule, order: newOrder, groupId: targetGroup.id },
        });
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <table className={styles.rulesTable}>
                <thead>
                    <tr>
                        {advancedRules && (
                            <>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                    )}
                                >
                                    Priority
                                </th>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                    )}
                                >
                                    Rule Group
                                </th>
                            </>
                        )}
                        <th className={classNames(styles.th, styles.header)}>
                            Conditions
                        </th>
                        <th className={classNames(styles.th, styles.header)}>
                            Actions
                        </th>
                        <th className={classNames(styles.th, styles.header)}>
                            Transactions
                        </th>
                    </tr>
                </thead>
                <SortableContext
                    items={groups.map((group) => group.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {groups.map((group, index) => (
                        <SortableGroup key={group.id} id={group.id}>
                            {({
                                listeners: groupListeners,
                                attributes: groupAttributes,
                            }: any) => (
                                <RulesGroupBody
                                    group={group}
                                    groupIndex={index}
                                    overId={overId}
                                    groupListeners={groupListeners}
                                    groupAttributes={groupAttributes}
                                    isGroupDragged={
                                        !!activeId && !!findGroupById(activeId)
                                    }
                                    counterparties={counterparties}
                                    transactionCounts={transactionCounts}
                                />
                            )}
                        </SortableGroup>
                    ))}
                </SortableContext>

                {advancedRules && accountingGroup && (
                    <AccountingRulesTable
                        priority={groups.length + 1}
                        accountingGroup={accountingGroup}
                        counterparties={counterparties}
                    />
                )}
            </table>

            <DragOverlay
                dropAnimation={null}
                className={styles.dragOverlayBody}
            >
                {activeId
                    ? (() => {
                          // If dragging a group
                          const overlayGroup = findGroupById(activeId);
                          if (overlayGroup) {
                              return (
                                  <GroupOverlay
                                      group={overlayGroup}
                                      groupIndex={groups.findIndex(
                                          (group) =>
                                              group.id === overlayGroup.id,
                                      )}
                                  />
                              );
                          }
                          // If dragging a rule
                          const overlayRule = findRuleById(activeId);
                          if (overlayRule) {
                              return (
                                  <RuleOverlay
                                      rule={overlayRule}
                                      counterparties={counterparties}
                                  />
                              );
                          }
                          return null;
                      })()
                    : null}
            </DragOverlay>
        </DndContext>
    );
};
